import { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';
import LogService from '../../services/LogService';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hayError: boolean;
}

// Si algo falla al pintar una pantalla, React desmonta TODA la aplicación y queda la pantalla en blanco, sin pista de qué
// ha pasado. Esto muestra un aviso con la opción de recargar en el lugar de la pantalla que ha fallado y registra el error.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hayError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hayError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        LogService.logError({
            method: 'ErrorBoundary',
            level: 'error',
            message: error.message,
            meta: { stack: error.stack, componentStack: info.componentStack, ruta: window.location.pathname },
        });
    }

    render() {
        if (!this.state.hayError) return this.props.children;
        return (
            <Box sx={{ p: 2, maxWidth: 640, m: '40px auto' }}>
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Recargar</Button>}
                >
                    Algo ha ido mal al mostrar esta pantalla. Recarga la página y, si vuelve a pasar, contacta con soporte.
                </Alert>
            </Box>
        );
    }
}

// Protección de las pantallas: al cambiar de ruta se reinicia (la `key`), así el menú sigue funcionando y se puede ir a otra
// pantalla sin recargar.
export const ErrorBoundaryRuta = ({ children }: ErrorBoundaryProps) => {
    const location = useLocation();
    return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

export default ErrorBoundary;
